import { FilterTypeEnum } from '@creditsights/api-ui-web';
import {
  ContentTypeFilter,
  ContentTypeFilterToContentTypeTag,
  DateFilterOption,
  EventDrivenTag,
  FeedType,
  Filter,
  FilterOption,
  FilterType,
  IssuerOption,
  PageType,
} from '../../models';
import { SearchResultCategory } from '../../pages/SearchResults/SearchResults.constants';

const GLOBAL_FEED_FILTERS: FilterOption[] = [
  FilterType.region,
  FilterType.sector,
  FilterType.creditGrade,
  FilterType.assetClass,
  FilterType.topic,
  DateFilterOption,
];

const SPECIAL_SITUATIONS_FEED_FILTERS: FilterOption[] = [
  FilterType.region,
  FilterType.sector,
  FilterType.assetClass,
  FilterType.topic,
  DateFilterOption,
];

const MY_FEED_FILTERS: FilterOption[] = [FilterType.assetClass, DateFilterOption];

const RECENTLY_PUBLISHED_FILTERS: FilterOption[] = [FilterType.creditGrade, FilterType.topic, DateFilterOption];

export const SEARCH_RESULTS_FILTERS: FilterOption[] = [
  FilterType.region,
  FilterType.analyst,
  FilterType.sector,
  FilterType.creditGrade,
  FilterType.assetClass,
  FilterType.topic,
  DateFilterOption,
];

export const EVENT_DRIVEN_REPORT = 'type-tag/event_driven_report';
export const NEWS = 'type-tag/news';

export const EVENT_DRIVEN_TITLES = {
  [EventDrivenTag.TopicalResearch]: 'Topical Research',
  [EventDrivenTag.LoansBonds]: 'Loans vs. Bonds',
  [EventDrivenTag.CovenantPrimers]: 'Covenant Primers',
  [EventDrivenTag.PlainEnglishGlossary]: 'Plain English Glossary',
  [EventDrivenTag.TrendlinesResearch]: 'Trendlines Research',
};

export const EVENT_DRIVEN_TITLE_CONTAINS = {
  [EventDrivenTag.LoansBonds]: 'Loans vs. Bonds',
  [EventDrivenTag.CovenantPrimers]: 'Primer',
  [EventDrivenTag.PlainEnglishGlossary]: 'Plain English',
  [EventDrivenTag.TrendlinesResearch]: 'Trendlines',
};

export const EVENT_TITLE_TAGS = {
  [EVENT_DRIVEN_TITLE_CONTAINS[EventDrivenTag.LoansBonds]]: [
    EVENT_DRIVEN_TITLES[EventDrivenTag.LoansBonds],
    EventDrivenTag.LoansBonds,
  ],
  [EVENT_DRIVEN_TITLE_CONTAINS[EventDrivenTag.CovenantPrimers]]: [
    EVENT_DRIVEN_TITLES[EventDrivenTag.CovenantPrimers],
    EventDrivenTag.CovenantPrimers,
  ],
  [EVENT_DRIVEN_TITLE_CONTAINS[EventDrivenTag.PlainEnglishGlossary]]: [
    EVENT_DRIVEN_TITLES[EventDrivenTag.PlainEnglishGlossary],
    EventDrivenTag.PlainEnglishGlossary,
  ],
  [EVENT_DRIVEN_TITLE_CONTAINS[EventDrivenTag.TrendlinesResearch]]: [
    EVENT_DRIVEN_TITLES[EventDrivenTag.TrendlinesResearch],
    EventDrivenTag.TrendlinesResearch,
  ],
};
export const NEWS_CATEGORIES = [
  {
    displayName: 'Daily Reports',
    pattern: /daily/,
  },
  {
    displayName: 'Weekly Reports',
    pattern: /weekly/,
  },
  {
    displayName: 'Monthly Reports',
    pattern: /monthly/,
  },
  {
    displayName: 'Quarterly Reports',
    pattern: /quarterly/,
  },
  {
    displayName: 'Insight Reports',
    pattern: /insights/,
  },
  {
    displayName: 'Events Calendar',
    pattern: /events_calendar/,
  },
];
export const FeedFilters: Record<FeedType, FilterOption[]> = {
  [FeedType.My]: MY_FEED_FILTERS,
  [FeedType.Global]: GLOBAL_FEED_FILTERS,
  [FeedType.SpecialSituations]: SPECIAL_SITUATIONS_FEED_FILTERS,
};

export const PageFilters: Record<PageType, FilterOption[]> = {
  [PageType.RecentlyPublished]: RECENTLY_PUBLISHED_FILTERS,
  [PageType.Search]: SEARCH_RESULTS_FILTERS,
};
export const IsFiltersBarExpandedKey = 'isFiltersBarExpanded';

export const CONTENT_TYPES_WITH_SOURCE_FILTERS = [
  ContentTypeFilter.ALL,
  ContentTypeFilter.COVENANTS,
  ContentTypeFilter.NEWS,
];
export const CONTENT_TYPES_WITH_SOURCE_CATEGORIES = [
  SearchResultCategory.All,
  SearchResultCategory.Covenants,
  SearchResultCategory.News,
];
export const getFeedFilters = (articleTypeFilter: ContentTypeFilter, feedType: FeedType): FilterOption[] => {
  const defaultFilters: FilterOption[] = FeedFilters[feedType];
  return CONTENT_TYPES_WITH_SOURCE_FILTERS.includes(articleTypeFilter)
    ? [FilterTypeEnum.source, ...defaultFilters]
    : defaultFilters;
};
export const getRecentlyPublishedFilters = (
  articleTypeFilter: ContentTypeFilter,
  pageType: PageType
): FilterOption[] => {
  const defaultFilters: FilterOption[] = PageFilters[pageType];
  return CONTENT_TYPES_WITH_SOURCE_FILTERS.includes(articleTypeFilter)
    ? [FilterTypeEnum.source, ...defaultFilters]
    : defaultFilters;
};

export const getSearchFilters = (articleTypeFilter: SearchResultCategory, pageType: PageType): FilterOption[] => {
  const defaultFilters: FilterOption[] = PageFilters[pageType];
  const filters = CONTENT_TYPES_WITH_SOURCE_CATEGORIES.includes(articleTypeFilter)
    ? [FilterTypeEnum.source, ...defaultFilters]
    : defaultFilters;

  return [IssuerOption, ...filters];
};

export const transformFilter = (filter: Filter, filterType?: ContentTypeFilter): Filter => {
  if (filter.type !== FilterTypeEnum.source) {
    return filter;
  }
  if (filterType) {
    const source = filter.options.find((option) => option.tagId === ContentTypeFilterToContentTypeTag[filterType]);
    if (source) {
      return {
        type: changeFilterName(filter.type, filterType),
        options: source.children ?? [],
      };
    }
  }
  return filter;
};
export const changeFilterName = (type: FilterTypeEnum, articleType?: ContentTypeFilter): FilterTypeEnum => {
  if (type !== FilterTypeEnum.source) {
    return type;
  }
  if (articleType === ContentTypeFilter.NEWS) {
    return FilterTypeEnum.news_source;
  }
  if (articleType === ContentTypeFilter.COVENANTS) {
    return FilterTypeEnum.covenants_source;
  }
  return type;
};

export const changeFilterNames = (filters: FilterType[], articleType?: ContentTypeFilter): FilterTypeEnum[] => {
  return filters.map((filter) => changeFilterName(filter, articleType));
};
